import React, { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { BarChart3, CalendarDays, RefreshCw } from "lucide-react";
import { IDashboardFilterRequest } from "../../types/Request/AdminRequest";
import AnalyticsSummary from "../../components/Admin/AnalyticsSummary";
import AnalyticsTrendChart from "../../components/Admin/AnalyticsTrendChart";
import BannerPerformance from "../../components/Admin/BannerPerformance";
import ProductPerformance from "../../components/Admin/ProductPerformance";
import TopSearches from "../../components/Admin/TopSearches";
import { useAnalyticsDashboardQuery } from "../../redux/actions/Admin/dashboardAdmin";

interface DashboardContextType {
  filterMode: "day" | "week" | "month" | "custom";

  headerDates?: {
    fromDate?: string;
    toDate?: string;
  };
}

const AdminAnalytics: React.FC = () => {
  const { filterMode, headerDates } = useOutletContext<DashboardContextType>();

  // ============================================================
  // FILTER
  // ============================================================

  const analyticsFilter: IDashboardFilterRequest = useMemo(() => {
    switch (filterMode) {
      case "day":
        return {
          period: "Today",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "month":
        return {
          period: "Monthly",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "custom":
        return {
          period: "Custom",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };

      case "week":
      default:
        return {
          period: "Weekly",
          startDate: headerDates?.fromDate,
          endDate: headerDates?.toDate,
        };
    }
  }, [filterMode, headerDates?.fromDate, headerDates?.toDate]);

  // ============================================================
  // API
  // ============================================================

  const {
    data: analyticsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useAnalyticsDashboardQuery(analyticsFilter);

  const analytics = analyticsResponse?.data;

  // ============================================================
  // LOADING
  // ============================================================

  if (isLoading) {
    return (
      <div className="px-1 pb-20">
        <div className="animate-pulse space-y-5">
          <div className="h-12 bg-gray-100 rounded-2xl" />

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-24 bg-gray-100 rounded-2xl" />
            ))}
          </div>

          <div className="h-[380px] bg-gray-100 rounded-2xl" />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="h-[420px] bg-gray-100 rounded-2xl" />

            <div className="h-[420px] bg-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="px-1 pb-20 space-y-5">
      {/* ======================================================
          PAGE HEADER
      ====================================================== */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-logoHeading/10 text-logoHeading flex items-center justify-center">
            <BarChart3 size={21} />
          </div>

          <div>
            <h1 className="text-xl font-bold text-gray-800">Analytics</h1>

            <p className="text-[11px] text-gray-400 mt-0.5">
              Customer engagement and website performance
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* CURRENT PERIOD */}

          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-100 shadow-sm">
            <CalendarDays size={14} className="text-logoHeading" />

            <span className="text-[11px] font-semibold text-gray-600 capitalize">
              {filterMode === "day"
                ? "Today"
                : filterMode === "week"
                  ? "This Week"
                  : filterMode === "month"
                    ? "This Month"
                    : "Custom Range"}
            </span>
          </div>

          {/* REFRESH */}

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="
              w-9
              h-9
              rounded-xl
              bg-white
              border border-gray-100
              shadow-sm
              flex
              items-center
              justify-center
              text-gray-500
              hover:text-logoHeading
              hover:border-logoHeading/20
              transition-all
              disabled:opacity-50
            "
            title="Refresh analytics"
          >
            <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ======================================================
          SUMMARY
      ====================================================== */}

      <AnalyticsSummary summary={analytics?.summary} />

      {/* ======================================================
          TREND
      ====================================================== */}

      <AnalyticsTrendChart trend={analytics?.trend} />

      {/* ======================================================
          BANNERS + SEARCHES
      ====================================================== */}

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5">
        <BannerPerformance banners={analytics?.topBanners} />

        <TopSearches searches={analytics?.topSearches} />
      </div>

      {/* ======================================================
          PRODUCTS
      ====================================================== */}

      <ProductPerformance products={analytics?.topProducts} />
    </div>
  );
};

export default AdminAnalytics;
